import {
  DIAS_SEMANA,
  type CatequistaOpcaoDto,
  type CreateTurmaInput,
  type TurmaDto,
} from '@sheepcount/shared';
import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChipToggle } from '@/components/ui/chip-toggle';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { PageLoading } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateBR } from '@/lib/format';

interface TurmaDraft {
  nome: string;
  dataInicio: string;
  dataFim: string;
  diasSemana: number[];
  inicioCiclo: string;
  metaPresenca: string;
  catequistaIds: string[];
}

const EMPTY_DRAFT: TurmaDraft = {
  nome: '',
  dataInicio: '',
  dataFim: '',
  diasSemana: [],
  inicioCiclo: '1',
  metaPresenca: '75',
  catequistaIds: [],
};

export function TurmasPage() {
  const toast = useToast();
  const { catequista, isAdmin } = useAuth();
  const [turmas, setTurmas] = useState<TurmaDto[] | null>(null);
  const [professores, setProfessores] = useState<CatequistaOpcaoDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TurmaDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [turmasData, professoresData] = await Promise.all([
        api<TurmaDto[]>('/turmas'),
        api<CatequistaOpcaoDto[]>('/catequistas/opcoes'),
      ]);
      setTurmas(turmasData);
      setProfessores(professoresData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar turmas');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openNew() {
    setEditingId(null);
    // Catequista não-admin sempre entra como professor da turma que cria
    // (a API força; aqui o chip já nasce marcado).
    setDraft({
      ...EMPTY_DRAFT,
      catequistaIds: !isAdmin && catequista ? [catequista.id] : [],
    });
    setSheetOpen(true);
  }

  function openEdit(turma: TurmaDto) {
    setEditingId(turma.id);
    setDraft({
      nome: turma.nome,
      dataInicio: turma.dataInicio,
      dataFim: turma.dataFim,
      diasSemana: [...turma.diasSemana],
      inicioCiclo: String(turma.inicioCiclo),
      metaPresenca: String(turma.metaPresenca),
      catequistaIds: turma.professores.map((p) => p.id),
    });
    setSheetOpen(true);
  }

  function toggleProfessor(id: string) {
    setDraft((d) => ({
      ...d,
      catequistaIds: d.catequistaIds.includes(id)
        ? d.catequistaIds.filter((x) => x !== id)
        : [...d.catequistaIds, id],
    }));
  }

  function toggleDia(dia: number) {
    setDraft((d) => ({
      ...d,
      diasSemana: d.diasSemana.includes(dia)
        ? d.diasSemana.filter((x) => x !== dia)
        : [...d.diasSemana, dia].sort((a, b) => a - b),
    }));
  }

  async function handleSave() {
    if (!draft.nome.trim()) {
      toast('Informe o nome da turma');
      return;
    }
    if (!draft.dataInicio || !draft.dataFim) {
      toast('Informe as datas de início e fim');
      return;
    }
    if (draft.diasSemana.length === 0) {
      toast('Escolha ao menos um dia de aula');
      return;
    }
    const meta = Number(draft.metaPresenca);
    const payload: CreateTurmaInput = {
      nome: draft.nome.trim(),
      dataInicio: draft.dataInicio,
      dataFim: draft.dataFim,
      diasSemana: draft.diasSemana,
      inicioCiclo: Number(draft.inicioCiclo),
      metaPresenca: Number.isFinite(meta) ? Math.min(100, Math.max(0, Math.round(meta))) : 75,
      catequistaIds: draft.catequistaIds,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api(`/turmas/${editingId}`, { method: 'PUT', body: payload });
      } else {
        await api('/turmas', { method: 'POST', body: payload });
      }
      setSheetOpen(false);
      toast(editingId ? 'Turma atualizada' : 'Turma criada');
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao salvar turma');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[22px] font-bold tracking-[-0.01em]">Turmas</h1>
        <Button size="sm" className="px-4 py-[9px]" onClick={openNew}>
          + Nova turma
        </Button>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3">
          <div className="text-[13px] text-danger">{error}</div>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Tentar novamente
          </Button>
        </div>
      ) : turmas === null ? (
        <PageLoading />
      ) : turmas.length === 0 ? (
        <div className="text-[13px] text-muted">Nenhuma turma cadastrada ainda.</div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5">
          {turmas.map((turma) => (
            <Card
              key={turma.id}
              role="button"
              tabIndex={0}
              onClick={() => openEdit(turma)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openEdit(turma);
              }}
              className="flex cursor-pointer flex-col gap-2.5 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
            >
              <div className="text-[15px] font-bold">{turma.nome}</div>
              <div className="flex flex-wrap gap-1.5">
                {turma.diasSemana.map((dia) => (
                  <Badge key={dia}>{DIAS_SEMANA[dia] ?? ''}</Badge>
                ))}
              </div>
              <div className="text-xs text-muted">
                {formatDateBR(turma.dataInicio)} a {formatDateBR(turma.dataFim)}
              </div>
              {turma.professores.length > 0 && (
                <div className="text-xs text-muted">
                  Prof.: {turma.professores.map((p) => p.nome).join(', ')}
                </div>
              )}
              <div className="mt-0.5 flex items-center justify-between">
                <div className="text-xs text-muted">{turma.numAlunos} alunos</div>
                <div className="text-xs font-semibold text-primary-dark">
                  Meta {turma.metaPresenca}%
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetTitle>{editingId ? 'Editar turma' : 'Nova turma'}</SheetTitle>
          <div className="flex flex-col gap-3.5">
            <div>
              <Label htmlFor="turma-nome">Nome da turma</Label>
              <Input
                id="turma-nome"
                className="mt-1.5"
                placeholder="Ex: Crisma - Jovens"
                value={draft.nome}
                onChange={(e) => setDraft((d) => ({ ...d, nome: e.target.value }))}
              />
            </div>
            <div className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="turma-inicio">Data início</Label>
                <Input
                  id="turma-inicio"
                  className="mt-1.5"
                  type="date"
                  value={draft.dataInicio}
                  onChange={(e) => setDraft((d) => ({ ...d, dataInicio: e.target.value }))}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="turma-fim">Data fim</Label>
                <Input
                  id="turma-fim"
                  className="mt-1.5"
                  type="date"
                  value={draft.dataFim}
                  onChange={(e) => setDraft((d) => ({ ...d, dataFim: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Dias de aula na semana</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {DIAS_SEMANA.map((label, dia) => (
                  <ChipToggle
                    key={label}
                    selected={draft.diasSemana.includes(dia)}
                    onClick={() => toggleDia(dia)}
                  >
                    {label}
                  </ChipToggle>
                ))}
              </div>
            </div>
            <div>
              <Label>Professores da turma</Label>
              {professores.length === 0 ? (
                <div className="mt-2 text-xs text-muted">Nenhum professor ativo.</div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {professores.map((professor) => (
                    <ChipToggle
                      key={professor.id}
                      selected={draft.catequistaIds.includes(professor.id)}
                      onClick={() => toggleProfessor(professor.id)}
                    >
                      {professor.nome}
                    </ChipToggle>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="turma-ciclo">Início da semana da turma</Label>
              <Select
                id="turma-ciclo"
                className="mt-1.5"
                value={draft.inicioCiclo}
                onChange={(e) => setDraft((d) => ({ ...d, inicioCiclo: e.target.value }))}
              >
                {DIAS_SEMANA.map((label, dia) => (
                  <option key={label} value={dia}>
                    {label}
                    {dia === 1 ? ' (padrão)' : ''}
                  </option>
                ))}
              </Select>
              <div className="mt-1 text-[11.5px] text-muted">
                Dias na mesma semana contam como uma só aula. Mude apenas se a reposição cai na
                semana seguinte (ex.: aula no Sáb e reposição na Qua → comece no Sáb).
              </div>
            </div>
            <div>
              <Label htmlFor="turma-meta">Meta mínima de presença (%)</Label>
              <Input
                id="turma-meta"
                className="mt-1.5"
                type="number"
                min={0}
                max={100}
                value={draft.metaPresenca}
                onChange={(e) => setDraft((d) => ({ ...d, metaPresenca: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-[22px] flex gap-2.5">
            <div className="flex-1" />
            <Button variant="secondary" disabled={saving} onClick={() => setSheetOpen(false)}>
              Cancelar
            </Button>
            <Button className="font-bold" disabled={saving} onClick={() => void handleSave()}>
              Salvar
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
